import React from 'react';
import { inject, observer } from 'mobx-react';
import { MCP_BUILTIN_SERVER_KEY } from '@kentik/ui-shared/kaizen/mcpBuiltinServers';
import { Callout, Flex, Text } from 'core/components';
import { OAuthControls } from 'app/components/McpOAuthControls';
import { InfobloxLogo } from 'app/components/InfobloxLogo';

// Built-in servers connect through their own routes and track their own connection state.
function connectionFor({ $mcpServers, $builtinMcpServers }, server) {
  if (server.builtinKey) {
    return {
      ...$builtinMcpServers.getOAuthStatus(server.builtinKey),
      connect: () => $builtinMcpServers.connect(server.builtinKey)
    };
  }

  const status = $mcpServers.oauthStatusMap.get(server.mcpServerConfigId) || {};

  return {
    ...status,
    connect: () => $mcpServers.connectOAuth(server.mcpServerConfigId, { serverName: server.displayName })
  };
}

const ServerConnect = ({ server, connection }) => {
  if (connection.connected && !connection.connecting) {
    return (
      <Text small muted>
        Connected to {server.displayName}. Ask again to use it.
      </Text>
    );
  }

  const isInfobloxIq = server.builtinKey === MCP_BUILTIN_SERVER_KEY.INFOBLOX_IQ;
  const builtinText = connection.error ? 'Retry' : 'Connect';

  return (
    <OAuthControls
      status={connection}
      connectText={`${server.builtinKey ? builtinText : 'Reconnect'} ${server.displayName}`}
      onConnect={connection.connect}
      icon={isInfobloxIq ? <InfobloxLogo size={14} /> : undefined}
      intent={server.builtinKey ? 'primary' : undefined}
      loading={connection.connecting && (!server.builtinKey || !connection.authorizeUrl)}
      inline={!!server.builtinKey}
    />
  );
};

/**
 * Connecting is a per-user action needing only ai.mcp_servers::read, so it belongs here rather than
 * in admin settings: members get MCP tools in chat but cannot reach the settings page at all.
 */
const McpAuthRequiredBanner = ({ $mcpServers, $builtinMcpServers, authRequiredTools }) => {
  if (!authRequiredTools || authRequiredTools.length === 0) {
    return null;
  }

  const servers = [
    ...new Map(
      authRequiredTools.filter((tool) => tool.mcpServerConfigId).map((tool) => [tool.mcpServerConfigId, tool])
    ).values()
  ];

  if (servers.length === 0) {
    return null;
  }

  return (
    <Callout intent="warning" icon="lock" mb={2}>
      <Text mb={1}>
        {servers.length === 1
          ? `AI Advisor needs access to "${servers[0].displayName}" before it can use those tools.`
          : `AI Advisor needs access to ${servers.length} MCP servers before it can use their tools.`}
      </Text>
      <Flex gap={1} flexWrap="wrap" alignItems="center">
        {servers.map((server) => (
          <ServerConnect
            key={server.mcpServerConfigId}
            server={server}
            connection={connectionFor({ $mcpServers, $builtinMcpServers }, server)}
          />
        ))}
      </Flex>
    </Callout>
  );
};

export default inject('$mcpServers', '$builtinMcpServers')(observer(McpAuthRequiredBanner));
